import { useEffect, useState, useCallback } from 'react';
import { questsService } from '../services/quests-service';
import type { QuestTemplateFirestore, QuestType } from '../services/questTypes';
import { DisplayQuestTemplate } from './DisplayQuestTemplate';
import { QuestTypeSelector } from './QuestTypeSelector';
import { BookOpen, ChevronDown, ChevronUp, GraduationCap, Lightbulb, MessageCircle, MessageSquare, NotebookPen, Star, Upload, Users, Wrench } from 'lucide-react';
import Button from '../../../components/Button';
import { ButtonWithConfirmation } from '../../../components/ButtonWithConfirmation';

interface JourneyDaySectionProps {
    journeyDayId: string;
    isOpen: boolean;
    isCurrentDayInJourney: boolean;
    isInsightsDay: boolean;
    availableJourneyDays: string[];
    questTypeCounts: Record<QuestType, { published: number; unpublished: number }>;
    onEdit: (questTemplate: QuestTemplateFirestore) => void;
    onDelete: (questTemplate: QuestTemplateFirestore) => Promise<boolean>;
    onCreateNew: (maxOrder: number, questType: QuestType) => void;
    onMoveToDay: (questTemplate: QuestTemplateFirestore, sourceJourneyDayId: string, targetJourneyDayId: string) => void;
    onToggle: () => void;
    refreshTrigger?: number;
}

// Helper function to get quest type icons
const getQuestTypeIcon = (type: QuestType): React.ReactNode => {
    const iconClass = "w-3 h-3";
    const icons: Record<QuestType, React.ReactNode> = {
        'chat': <MessageCircle className={iconClass} />,
        'check-in': <NotebookPen className={iconClass} />,
        'story': <BookOpen className={iconClass} />,
        'lesson': <GraduationCap className={iconClass} />,
        'community-question': <Users className={iconClass} />,
        'tool': <Wrench className={iconClass} />,
        'feedback': <MessageSquare className={iconClass} />,
        'rate': <Star className={iconClass} />
    };
    return icons[type];
};

// Helper function to get quest type labels
const getQuestTypeLabel = (type: QuestType): string => {
    const labels: Record<QuestType, string> = {
        'chat': 'Chat',
        'check-in': 'Check-in',
        'story': 'Story',
        'lesson': 'Lesson',
        'community-question': 'Community',
        'tool': 'Tool',
        'feedback': 'Feedback',
        'rate': 'Rate'
    };
    return labels[type];
};

// Helper function to get quest type colors
const getQuestTypeColor = (type: QuestType): string => {
    const colors: Record<QuestType, string> = {
        'chat': 'bg-blue-500',
        'check-in': 'bg-green-500',
        'story': 'bg-purple-500',
        'lesson': 'bg-indigo-500',
        'community-question': 'bg-pink-500',
        'tool': 'bg-orange-500',
        'feedback': 'bg-teal-500',
        'rate': 'bg-yellow-500'
    };
    return colors[type];
};

export const JourneyDaySection = ({ journeyDayId, isOpen, isCurrentDayInJourney, isInsightsDay, availableJourneyDays, questTypeCounts, onEdit, onDelete, onCreateNew, onMoveToDay, onToggle, refreshTrigger }: JourneyDaySectionProps) => {
    const [questTemplates, setQuestTemplates] = useState<QuestTemplateFirestore[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showTypeSelector, setShowTypeSelector] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const fetchQuestTemplates = async () => {
            const questTemplates = await questsService.getQuestTemplates(journeyDayId);
            // Sort by order to ensure consistency
            questTemplates.sort((a, b) => a.order - b.order);
            setQuestTemplates(questTemplates);
            console.log('questTemplates', questTemplates);
        };
        fetchQuestTemplates();
    }, [journeyDayId, isOpen, refreshTrigger]);

    const handleMoveQuest = useCallback(async (index: number, direction: 'up' | 'down') => {
        const newQuestTemplates = [...questTemplates];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newQuestTemplates.length) {
            return;
        }

        // Swap the quests
        [newQuestTemplates[index], newQuestTemplates[targetIndex]] = [newQuestTemplates[targetIndex], newQuestTemplates[index]];

        // Update their order values to match their new positions
        newQuestTemplates.forEach((quest, i) => {
            quest.order = i;
        });

        // Update local state immediately for responsive UI
        setQuestTemplates(newQuestTemplates);

        // Update in Firebase
        await questsService.updateQuestOrders(journeyDayId, newQuestTemplates);
    }, [journeyDayId, questTemplates]);

    const handleDeleteQuest = useCallback(async (questTemplate: QuestTemplateFirestore) => {
        setIsDeleting(true);
        try {
            // Delete the quest
            const deleted = await onDelete(questTemplate);
            if (!deleted) {
                setIsDeleting(false);
                return;
            }

            // Get the remaining quests (excluding the deleted one)
            const remainingQuests = questTemplates.filter(q => q.id !== questTemplate.id);

            // Reorder the remaining quests
            remainingQuests.forEach((quest, index) => {
                quest.order = index;
            });

            // Update local state immediately
            setQuestTemplates(remainingQuests);

            // Update orders in Firebase for all remaining quests
            if (remainingQuests.length > 0) {
                await questsService.updateQuestOrders(journeyDayId, remainingQuests);
            }
        } finally {
            setIsDeleting(false);
        }
    }, [journeyDayId, questTemplates, onDelete]);

    const handlePublishAllQuests = useCallback(async () => {
        await questsService.publishAllQuests(journeyDayId, questTemplates);

        // Update local state to reflect the published status
        const updatedQuests = questTemplates.map(quest => ({ ...quest, isPublished: true }));
        setQuestTemplates(updatedQuests);
    }, [journeyDayId, questTemplates]);

    const unpublishedCount = questTemplates.filter(q => !q.isPublished).length;
    const hasUnpublishedQuests = unpublishedCount > 0;

    // Get quest types with non-zero counts
    const questTypesWithCounts = (Object.entries(questTypeCounts) as [QuestType, { published: number; unpublished: number }][])
        .filter(([_, counts]) => (counts.published + counts.unpublished) > 0)
        .sort((a, b) => (b[1].published + b[1].unpublished) - (a[1].published + a[1].unpublished)); // Sort by total count descending

    return (
        <div>
            <div className={`flex justify-between items-center gap-2 rounded-lg py-2 px-4 cursor-pointer ${isCurrentDayInJourney ? 'bg-green-700 text-white' : 'bg-blue-100'}`} onClick={() => onToggle()}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                    <h1 className="text-lg font-bold">{journeyDayId} {isCurrentDayInJourney ? <span className="text-white">- Current Day in Journey</span> : <></>}</h1>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {questTypesWithCounts.map(([type, counts]) => {
                            const total = counts.published + counts.unpublished;
                            const displayText = counts.unpublished === 0
                                ? `(${total})`
                                : `(${counts.published}/${total})`;

                            return (
                                <span
                                    key={type}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white ${getQuestTypeColor(type)}`}
                                >
                                    {getQuestTypeIcon(type)}
                                    <span>{getQuestTypeLabel(type)}</span>
                                    <span>{displayText}</span>
                                </span>
                            );
                        })}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {isInsightsDay && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-500 text-white">
                            <Lightbulb className="w-3 h-3" />
                            Insights Day
                        </span>
                    )}
                    {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
            </div>

            <div className={`space-y-3 mt-2 ${isOpen ? 'block' : 'hidden'}`}>
                {questTemplates.map((questTemplate, index) => (
                    <DisplayQuestTemplate
                        key={`${questTemplate.id}-${index}`}
                        questTemplate={questTemplate}
                        journeyDayId={journeyDayId}
                        availableJourneyDays={availableJourneyDays}
                        onEdit={() => onEdit(questTemplate)}
                        onDelete={() => handleDeleteQuest(questTemplate)}
                        onMoveUp={() => handleMoveQuest(index, 'up')}
                        onMoveDown={() => handleMoveQuest(index, 'down')}
                        onMoveToDay={(targetJourneyDayId) => onMoveToDay(questTemplate, journeyDayId, targetJourneyDayId)}
                        canMoveUp={index > 0 && !isDeleting}
                        canMoveDown={index < questTemplates.length - 1 && !isDeleting}
                    />
                ))}

                {/* Create New Quest Button */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center flex flex-wrap gap-2 justify-center">
                    <Button
                        onClick={() => setShowTypeSelector(true)}
                        variant="primary"
                        className="w-full max-w-xs"
                    >
                        + Create New Quest Template
                    </Button>
                    {hasUnpublishedQuests && (
                        <ButtonWithConfirmation
                            onClick={handlePublishAllQuests}
                            firstText={`Publish All (${unpublishedCount})`}
                            secondText={`Publish ${unpublishedCount} quest${unpublishedCount > 1 ? 's' : ''}?`}
                            firstIcon={<Upload className="w-4 h-4" />}
                            secondIcon={<Upload className="w-4 h-4" />}
                            firstVariant="outline"
                            secondVariant="primary"
                            size="sm"
                        />
                    )}
                </div>
            </div>

            {/* Quest Type Selector Modal */}
            {showTypeSelector && (
                <QuestTypeSelector
                    onSelect={(questType) => {
                        const maxOrder = questTemplates.length > 0
                            ? Math.max(...questTemplates.map(q => q.order))
                            : -1;
                        onCreateNew(maxOrder + 1, questType);
                        setShowTypeSelector(false);
                    }}
                    onCancel={() => setShowTypeSelector(false)}
                />
            )}
        </div>
    );
};
